import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, MagnifyingGlass, EnvelopeSimple, ChatCircleDots, CheckCircle, Trash, Calendar, Clock } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';
import { confirmDialog } from '@/components/ConfirmDialog';

const TIP_LABELS = {
  rsv: 'RSV — Registar Stvarnih Vlasnika',
  fi_pd: 'Finansijski iskaz + Porez na dobit',
  gppfl: 'GPPFL — Godišnja Prijava Poreza Fizičkih Lica',
};

const STATUS_LABELS = {
  nije_radjeno: 'Nije rađeno',
  u_toku: 'U toku',
  predato: 'Predato',
};

const STATUS_COLORS = {
  nije_radjeno: 'bg-red-50 text-red-700 border-red-200',
  u_toku: 'bg-amber-50 text-amber-700 border-amber-200',
  predato: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function formatEur(v) {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function GodisnjeObavezePage() {
  const [tipActive, setTipActive] = useState('rsv');
  return (
    <div className="space-y-6" data-testid="godisnje-page">
      <div>
        <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
          <Calendar size={22} weight="duotone" className="text-blue-600" />
          Godišnje obaveze
        </h3>
        <p className="text-sm text-slate-500 mt-1">Praćenje godišnjih obaveza po klijentima — RSV, FI+PD, GPPFL</p>
      </div>

      <Tabs value={tipActive} onValueChange={setTipActive}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="rsv" data-testid="tab-rsv">RSV (DOO)</TabsTrigger>
          <TabsTrigger value="fi_pd" data-testid="tab-fi-pd">FI + Porez na dobit (DOO/NVO)</TabsTrigger>
          <TabsTrigger value="gppfl" data-testid="tab-gppfl">GPPFL (Preduzetnici)</TabsTrigger>
        </TabsList>
        <TabsContent value="rsv" className="mt-4"><GodisnjaTab tip="rsv" /></TabsContent>
        <TabsContent value="fi_pd" className="mt-4"><GodisnjaTab tip="fi_pd" /></TabsContent>
        <TabsContent value="gppfl" className="mt-4"><GodisnjaTab tip="gppfl" /></TabsContent>
      </Tabs>
    </div>
  );
}

function tipLabelShort(tip) {
  if (tip === 'rsv') return 'RSV';
  if (tip === 'fi_pd') return 'FI+PD';
  return 'GPPFL';
}

function GodisnjaTab({ tip }) {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('nije_radjeno');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // entry being edited
  const [editForm, setEditForm] = useState({ status: 'nije_radjeno', iznos_pd: '', datum_predaje: '', napomena: '' });
  const [sending, setSending] = useState({}); // {entryId: 'email'|'viber'}

  const [createOpen, setCreateOpen] = useState(false);
  const [createGodina, setCreateGodina] = useState(new Date().getFullYear() - 1);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['admin', 'super_admin'].includes(currentUser.role);

  const loadReports = async () => {
    setLoading(true);
    try {
      const r = await api.get('/godisnje/reports', { params: { tip } });
      setReports(r.data || []);
      if (r.data?.length && !selectedReport) {
        setSelectedReport(r.data[0]);
      }
    } catch (e) {
      toast.error('Greska pri ucitavanju');
    } finally {
      setLoading(false);
    }
  };

  const loadEntries = async (reportId) => {
    if (!reportId) { setEntries([]); return; }
    try {
      const r = await api.get(`/godisnje/reports/${reportId}/entries`);
      setEntries(r.data || []);
    } catch (e) {
      toast.error('Greska pri ucitavanju stavki');
    }
  };

  useEffect(() => { loadReports(); /* eslint-disable-next-line */ }, [tip]);
  useEffect(() => { if (selectedReport) loadEntries(selectedReport.id); }, [selectedReport]);

  const handleCreateReport = async () => {
    const godina = parseInt(createGodina);
    if (isNaN(godina) || godina < 2000 || godina > 2100) {
      toast.error('Neispravna godina');
      return;
    }
    try {
      const r = await api.post('/godisnje/reports', { tip, godina });
      toast.success(r.data.message);
      setCreateOpen(false);
      await loadReports();
      const fresh = await api.get('/godisnje/reports', { params: { tip } });
      const newOne = (fresh.data || []).find(x => x.id === r.data.id);
      if (newOne) setSelectedReport(newOne);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska');
    }
  };

  const handleDeleteReport = async () => {
    if (!selectedReport) return;
    if (!await confirmDialog({
      title: 'Brisanje godišnje tabele',
      message: `Obrisati ${TIP_LABELS[tip]} ${selectedReport.godina}? Ovo briše sve unose.`,
      confirmText: 'Obriši', variant: 'danger',
    })) return;
    try {
      await api.delete(`/godisnje/reports/${selectedReport.id}`);
      toast.success('Obrisano');
      setSelectedReport(null);
      setEntries([]);
      await loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska');
    }
  };

  const startEdit = (entry) => {
    setEditing(entry);
    setEditForm({
      status: entry.status || 'nije_radjeno',
      iznos_pd: entry.iznos_pd || 0,
      datum_predaje: entry.datum_predaje || '',
      napomena: entry.napomena || '',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        status: editForm.status,
        napomena: editForm.napomena,
        datum_predaje: editForm.datum_predaje,
      };
      if (tip === 'fi_pd') {
        payload.iznos_pd = parseFloat(String(editForm.iznos_pd).replace(',', '.')) || 0;
      }
      await api.patch(`/godisnje/entries/${editing.id}`, payload);
      toast.success('Sačuvano');
      setEditing(null);
      loadEntries(selectedReport.id);
      loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska');
    }
  };

  const sendNotif = async (entry, channel) => {
    setSending(s => ({ ...s, [entry.id]: channel }));
    try {
      const r = await api.post(`/godisnje/entries/${entry.id}/send-${channel}`);
      toast.success(r.data.message);
      loadEntries(selectedReport.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska');
    } finally {
      setSending(s => { const n = { ...s }; delete n[entry.id]; return n; });
    }
  };

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    return entries.filter(e => {
      if (filterStatus !== 'all' && e.status !== filterStatus) return false;
      if (!q) return true;
      return (e.naziv || '').toLowerCase().includes(q) || (e.pib || '').toLowerCase().includes(q);
    });
  }, [entries, search, filterStatus]);

  const ukupnoPd = useMemo(() => {
    if (tip !== 'fi_pd') return 0;
    return entries.reduce((sum, e) => sum + (parseFloat(e.iznos_pd) || 0), 0);
  }, [entries, tip]);

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <Card className="p-4 bg-white border-slate-200">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1 min-w-[220px]">
            <label className="text-xs font-semibold uppercase text-slate-500">Godina</label>
            <Select value={selectedReport?.id || ''} onValueChange={(v) => {
              const r = reports.find(x => x.id === v);
              setSelectedReport(r || null);
            }}>
              <SelectTrigger className="bg-white border-slate-300" data-testid={`${tip}-report-select`}>
                <SelectValue placeholder="Izaberite godinu" />
              </SelectTrigger>
              <SelectContent>
                {reports.length === 0 && <div className="px-2 py-1 text-xs text-slate-500">Nema tabela</div>}
                {reports.map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.godina} — {r.done}/{r.total} predato
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => { setCreateGodina(new Date().getFullYear() - 1); setCreateOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`${tip}-new-report`}>
            <Plus size={16} className="mr-2" /> Formiraj godišnju obavezu {tipLabelShort(tip)}
          </Button>
          {selectedReport && isAdmin && (
            <Button onClick={handleDeleteReport} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
              <Trash size={14} className="mr-2" /> Obriši tabelu
            </Button>
          )}
          {selectedReport && (
            <div className="ml-auto flex items-center gap-3 text-sm">
              <span className="text-slate-500">Predato:</span>
              <span className="font-bold text-emerald-600">{selectedReport.done}/{selectedReport.total}</span>
              {tip === 'fi_pd' && (
                <>
                  <span className="text-slate-300">|</span>
                  <span className="text-slate-500">Ukupno PD:</span>
                  <span className="font-bold text-blue-700">{formatEur(ukupnoPd)} EUR</span>
                </>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Filter */}
      {selectedReport && (
        <Card className="p-3 bg-white border-slate-200">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[260px] max-w-md">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pretraga po nazivu/PIB-u..." className="pl-9 bg-white border-slate-300" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="bg-white border-slate-300 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Svi statusi</SelectItem>
                <SelectItem value="nije_radjeno">Nije rađeno</SelectItem>
                <SelectItem value="u_toku">U toku</SelectItem>
                <SelectItem value="predato">Predato</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      {/* Table */}
      {selectedReport && (
        <Card className="overflow-hidden bg-white border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-semibold tracking-wider uppercase text-slate-600">
                  <th className="px-3 py-3 w-10">#</th>
                  <th className="px-3 py-3">{tip === 'gppfl' ? 'Ime i prezime' : 'Naziv klijenta'}</th>
                  <th className="px-3 py-3">{tip === 'gppfl' ? 'JMB' : 'PIB'}</th>
                  {tip === 'fi_pd' && <th className="px-3 py-3 text-right">Iznos PD (EUR)</th>}
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Predato</th>
                  {tip === 'fi_pd' && <th className="px-3 py-3 text-center">Notif.</th>}
                  <th className="px-3 py-3">Napomena</th>
                  <th className="px-3 py-3 text-right">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Učitavanje...</td></tr>}
                {!loading && filtered.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Nema rezultata</td></tr>}
                {filtered.map(e => (
                  <tr key={e.id} onDoubleClick={() => startEdit(e)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" title="Dvoklik za uređivanje" data-testid={`entry-${e.id}`}>
                    <td className="px-3 py-2 text-slate-500">{e.redni_broj}</td>
                    <td className="px-3 py-2 font-medium text-slate-900">{e.naziv}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{e.pib || '-'}</td>
                    {tip === 'fi_pd' && (
                      <td className="px-3 py-2 text-right font-mono text-sm">
                        {e.iznos_pd > 0 ? <span className="font-bold text-blue-700">{formatEur(e.iznos_pd)}</span> : <span className="text-slate-300">0,00</span>}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wider ${STATUS_COLORS[e.status] || ''}`}>
                        {STATUS_LABELS[e.status] || e.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{e.datum_predaje ? formatDate(e.datum_predaje) : '-'}</td>
                    {tip === 'fi_pd' && (
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {e.email_poslat && <span title="Email poslat" className="w-2 h-2 rounded-full bg-blue-500" />}
                          {e.viber_poslat && <span title="Viber poslat" className="w-2 h-2 rounded-full bg-purple-500" />}
                          {!e.email_poslat && !e.viber_poslat && <span className="text-slate-300 text-xs">-</span>}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-[200px] truncate">{e.napomena || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {tip === 'fi_pd' && (
                          <>
                            <button onClick={() => sendNotif(e, 'email')} disabled={sending[e.id] === 'email' || !e.iznos_pd} className="p-1.5 rounded hover:bg-blue-100 disabled:opacity-30" title="Pošalji email" data-testid={`send-email-${e.id}`}>
                              {sending[e.id] === 'email' ? <Clock size={14} className="text-blue-600 animate-spin" /> : <EnvelopeSimple size={14} className="text-blue-600" />}
                            </button>
                            <button onClick={() => sendNotif(e, 'viber')} disabled={sending[e.id] === 'viber' || !e.iznos_pd} className="p-1.5 rounded hover:bg-purple-100 disabled:opacity-30" title="Pošalji Viber" data-testid={`send-viber-${e.id}`}>
                              {sending[e.id] === 'viber' ? <Clock size={14} className="text-purple-600 animate-spin" /> : <ChatCircleDots size={14} className="text-purple-600" />}
                            </button>
                          </>
                        )}
                        <Button size="sm" variant="outline" onClick={() => startEdit(e)} className="h-7 text-xs" data-testid={`edit-${e.id}`}>Uredi</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create-report dialog */}
      {createOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setCreateOpen(false)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Calendar size={20} className="text-blue-600" />
              Formiraj godišnju obavezu — {tipLabelShort(tip)}
            </h3>
            <p className="text-sm text-slate-500">Sistem će automatski dodati sve {tip === 'gppfl' ? 'fizička lica iz baze' : tip === 'rsv' ? 'aktivne DOO klijente' : 'aktivne DOO i NVO klijente'} kao stavke.</p>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Godina</label>
              <Input
                type="number" min="2020" max="2100"
                value={createGodina}
                onChange={(e) => setCreateGodina(e.target.value)}
                className="bg-white border-slate-300 text-lg font-bold"
                autoFocus
                data-testid="create-godina-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Otkaži</Button>
              <Button onClick={handleCreateReport} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="confirm-create-report">
                <Plus size={14} className="mr-2" /> Formiraj
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit dialog */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold">Uredi: {editing.naziv}</h3>

            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Status</label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nije_radjeno">Nije rađeno</SelectItem>
                  <SelectItem value="u_toku">U toku</SelectItem>
                  <SelectItem value="predato">Predato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {tip === 'fi_pd' && (
              <div>
                <label className="text-xs font-semibold uppercase text-slate-500">Iznos poreza na dobit (EUR)</label>
                <Input type="text" inputMode="decimal" value={editForm.iznos_pd} onChange={(e) => setEditForm({ ...editForm, iznos_pd: e.target.value })} placeholder="0,00" className="bg-white border-slate-300" />
              </div>
            )}

            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Datum predaje</label>
              <Input type="date" value={editForm.datum_predaje} onChange={(e) => setEditForm({ ...editForm, datum_predaje: e.target.value })} className="bg-white border-slate-300" />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Napomena</label>
              <Input value={editForm.napomena} onChange={(e) => setEditForm({ ...editForm, napomena: e.target.value })} className="bg-white border-slate-300" />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" onClick={() => setEditing(null)}>Otkaži</Button>
              <Button onClick={saveEdit} className="bg-blue-600 hover:bg-blue-700 text-white">
                <CheckCircle size={14} className="mr-2" /> Sačuvaj
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
